// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export function isExportModel(source: any): source is IExport {
    // Method Implementation: simple
    return source.modelName === 'Export';
}

export function isFieldModel(source: any): source is IField {
    // Method Implementation: simple
    return source.modelName === 'Field';
}

export interface ExportZodSchemaShape {
    fields?: FieldZodSchemaShape[];
}

export interface FieldZodSchemaShape {
    name?: string;

    source?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const ExportZodSchema: Zod.ZodSchema<ExportZodSchemaShape> = Zod.lazy(() => Zod.object({
    fields: Zod.array(FieldZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldZodSchema: Zod.ZodSchema<FieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    source: Zod.string().optional()
}).nonstrict());

export interface IExport extends Wellspring.IModel {
    readonly modelName: string;

    readonly fields: Field[];

    readonly isFieldsDefined: boolean;
}

export interface IField extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly source: string;

    readonly isNameDefined: boolean;

    readonly isSourceDefined: boolean;
}

export class Export implements IExport {
    public static readonly modelName: string = 'Export';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'fields' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Export.modelName;
    }

    private _fields: Field[];

    public get fields(): Field[] {
        // Method Implementation: simple
        return this._fields;
    }

    public set fields(value: Field[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    private _isFieldsDefined: boolean;

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public constructor();
    public constructor(source: IExport);
    public constructor(source: Partial<Pick<Export, 'fields'>>);
    public constructor(source?: IExport | Partial<Pick<Export, 'fields'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isExportModel(source)) {
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
            else {
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
        }
        else {
            this._fields = [];
            this._isFieldsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Export.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'fields':
                return this.fields;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: Field = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Field.modelName) {
                                item = new Field();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Field.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Field;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Field();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Export {
        // Method Implementation: simple
        const schema = ExportZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ExportZodSchemaShape): Export {
        // Method Implementation: parseZodSchema
        return new Export({
            fields: schema.fields?.map(i => Field.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
        };
    }

    public clone(): Export {
        // Method Implementation: clone
        return new Export({
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined
        });
    }
}

export class Field implements IField {
    public static readonly modelName: string = 'Field';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'source' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Field.modelName;
    }

    private _name: string;

    private _source: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    private _isNameDefined: boolean;

    private _isSourceDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public constructor();
    public constructor(source: IField);
    public constructor(source: Partial<Pick<Field, 'name' | 'source'>>);
    public constructor(source?: IField | Partial<Pick<Field, 'name' | 'source'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Field.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'source':
                return this.source;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Field {
        // Method Implementation: simple
        const schema = FieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldZodSchemaShape): Field {
        // Method Implementation: parseZodSchema
        return new Field({
            name: schema.name,
            source: schema.source
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
        };
    }

    public clone(): Field {
        // Method Implementation: clone
        return new Field({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Export':
                return new Export();
            case 'Field':
                return new Field();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Export':
                return new Export(source);
            case 'Field':
                return new Field(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Export':
                return Export.parse(source);
            case 'Field':
                return Field.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
